(() => {
  const ROOT_SELECTOR = '[data-fr-product-root]';

  class FrProductController {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.section;
      this.purchaseMode = root.dataset.purchaseMode || 'standard';
      this.customCtaLabel = root.dataset.frCtaLabel || '';
      this.stickyCart = root.querySelector('fr-sticky-cart');
      this.stickyButton = this.stickyCart?.querySelector('[data-fr-sticky-button]');
      this.stickyPrice = this.stickyCart?.querySelector('[data-fr-sticky-price]');
      this.productForm = this.root.querySelector(`#product-form-${this.sectionId}`);
      this.detailDialog = this.root.querySelector('[data-fr-details-dialog]');
      this.detailDialogTitle = this.detailDialog?.querySelector('[data-fr-details-dialog-title]');
      this.detailDialogClose = this.detailDialog?.querySelector('[data-fr-details-close]');
      this.detailTrigger = null;
      this.detailScrollY = null;
      this.detailBodyStyles = null;
      this.syncQueued = false;

      this.sync = this.sync.bind(this);
      this.queueSync = this.queueSync.bind(this);
      this.updateStickyVisibility = this.updateStickyVisibility.bind(this);
      this.setStickyVisibility = this.setStickyVisibility.bind(this);
      this.handleSellingPlanChange = this.handleSellingPlanChange.bind(this);
      this.handleDetailNavigation = this.handleDetailNavigation.bind(this);
      this.closeDetailDialog = this.closeDetailDialog.bind(this);
      this.handleDetailDialogClick = this.handleDetailDialogClick.bind(this);
      this.handleDetailDialogClose = this.handleDetailDialogClose.bind(this);

      this.setupStickyCart();
      this.setupDetailDialog();
      this.root.addEventListener('change', this.handleSellingPlanChange);
      this.root.addEventListener('click', this.handleDetailNavigation);
      this.observeProductState();
      this.sync();
    }

    get mainButton() {
      return this.root.querySelector(`#ProductSubmitButton-${this.sectionId}`);
    }

    get mainButtonLabel() {
      return this.mainButton?.querySelector('span');
    }

    setupDetailDialog() {
      if (!this.detailDialog) return;
      this.detailDialogClose?.addEventListener('click', this.closeDetailDialog);
      this.detailDialog.addEventListener('click', this.handleDetailDialogClick);
      this.detailDialog.addEventListener('close', this.handleDetailDialogClose);
    }

    closeDetailDialog() {
      if (!this.detailDialog?.hasAttribute('open')) return;
      if (typeof this.detailDialog.close === 'function') {
        this.detailDialog.close();
        return;
      }
      this.detailDialog.removeAttribute('open');
      this.handleDetailDialogClose();
    }

    handleDetailDialogClick(event) {
      if (event.target === this.detailDialog) this.closeDetailDialog();
    }

    handleDetailDialogClose() {
      this.unlockDetailScroll();
      this.root.querySelectorAll('[data-fr-detail-link]').forEach((item) => {
        item.classList.remove('is-active');
        item.setAttribute('aria-expanded', 'false');
      });
      this.detailDialog?.querySelectorAll('.fr-product__accordion').forEach((item) => {
        item.hidden = false;
      });
      this.detailTrigger?.focus();
      this.detailTrigger = null;
    }

    lockDetailScroll() {
      if (document.documentElement.classList.contains('fr-product-details-open')) return;
      this.detailScrollY = window.scrollY;
      this.detailBodyStyles = {
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        overflow: document.body.style.overflow,
      };
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.detailScrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.documentElement.classList.add('fr-product-details-open');
    }

    unlockDetailScroll() {
      if (document.querySelector('[data-fr-details-dialog][open]')) return;
      document.documentElement.classList.remove('fr-product-details-open');
      if (!this.detailBodyStyles) return;
      document.body.style.position = this.detailBodyStyles.position;
      document.body.style.top = this.detailBodyStyles.top;
      document.body.style.width = this.detailBodyStyles.width;
      document.body.style.overflow = this.detailBodyStyles.overflow;
      window.scrollTo(0, this.detailScrollY || 0);
      this.detailBodyStyles = null;
      this.detailScrollY = null;
    }

    setupStickyCart() {
      if (!this.stickyCart || !this.stickyButton) return;

      this.setStickyVisibility(false);
      this.stickyButton.addEventListener('click', () => {
        const button = this.mainButton;
        if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
        button.click();
      });

      this.primaryAction = this.root.querySelector('.fr-product__buy .product-form__buttons');
      if (!this.primaryAction) return;

      if ('IntersectionObserver' in window) {
        this.actionObserver = new IntersectionObserver(this.updateStickyVisibility, {
          threshold: 0,
          rootMargin: '-1px 0px 0px 0px',
        });
        this.actionObserver.observe(this.primaryAction);
      } else {
        this.fallbackScrollHandler = () => this.setStickyVisibilityFromPosition();
        window.addEventListener('scroll', this.fallbackScrollHandler, { passive: true });
        window.addEventListener('resize', this.fallbackScrollHandler, { passive: true });
        this.setStickyVisibilityFromPosition();
      }
    }

    updateStickyVisibility(entries) {
      const entry = entries[0];
      if (!entry) return;
      const hasPassedPrimaryAction = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.setStickyVisibility(hasPassedPrimaryAction);
    }

    setStickyVisibilityFromPosition() {
      if (!this.primaryAction || !this.stickyCart) return;
      this.setStickyVisibility(this.primaryAction.getBoundingClientRect().bottom < 0);
    }

    setStickyVisibility(visible) {
      if (!this.stickyCart || !this.stickyButton) return;

      if (visible) {
        this.stickyCart.hidden = false;
        this.stickyCart.inert = false;
        this.stickyCart.removeAttribute('aria-hidden');
        this.stickyButton.removeAttribute('tabindex');
        window.requestAnimationFrame(() => this.stickyCart?.classList.add('is-visible'));
        return;
      }

      if (this.stickyCart.contains(document.activeElement)) this.mainButton?.focus();
      this.stickyCart.classList.remove('is-visible');
      this.stickyCart.inert = true;
      this.stickyCart.setAttribute('aria-hidden', 'true');
      this.stickyButton.setAttribute('tabindex', '-1');
      this.stickyCart.hidden = true;
    }

    observeProductState() {
      this.stateObserver = new MutationObserver(this.queueSync);
      const button = this.mainButton;
      const price = this.root.querySelector(`#price-${this.sectionId}`);

      if (button) {
        this.stateObserver.observe(button, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          characterData: true,
          subtree: true,
        });
      }

      if (price) {
        this.stateObserver.observe(price, {
          childList: true,
          characterData: true,
          subtree: true,
        });
      }
    }

    queueSync() {
      if (this.syncQueued) return;
      this.syncQueued = true;
      window.requestAnimationFrame(() => {
        this.syncQueued = false;
        this.sync();
      });
    }

    getVisiblePriceText() {
      const priceRoot = this.root.querySelector(`#price-${this.sectionId}`);
      if (!priceRoot) return '';

      const salePrice = priceRoot.querySelector('.price--on-sale .price-item--sale');
      const regularPrice = priceRoot.querySelector('.price__regular .price-item--regular');
      const fallbackPrice = priceRoot.querySelector('.price-item');
      return (salePrice || regularPrice || fallbackPrice)?.textContent?.trim() || '';
    }

    handleSellingPlanChange(event) {
      const select = event.target.closest?.('[data-fr-selling-plan]');
      if (!select || !this.root.contains(select)) return;

      const option = select.selectedOptions?.[0];
      const container = select.closest('.fr-selling-plan');
      if (!option || !container) return;

      const total = container.querySelector('[data-fr-plan-total]');
      const now = container.querySelector('[data-fr-plan-now]');
      const remaining = container.querySelector('[data-fr-plan-remaining]');
      const remainingRow = container.querySelector('[data-fr-plan-remaining-row]');

      if (total) total.textContent = option.dataset.frPlanTotal || '';
      if (now) now.textContent = option.dataset.frPlanNow || '';
      if (remaining) remaining.textContent = option.dataset.frPlanRemaining || '';
      if (remainingRow) remainingRow.hidden = option.dataset.frPlanHasRemaining !== 'true';
      if (this.stickyPrice && option.dataset.frPlanTotal) {
        this.stickyPrice.textContent = option.dataset.frPlanTotal;
      }

      const url = new URL(window.location.href);
      url.searchParams.set('selling_plan', option.value);
      this.root.querySelector('share-button')?.updateUrl?.(url.toString());
      if (this.root.dataset.updateUrl !== 'false') {
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }
      this.sync();
    }

    handleDetailNavigation(event) {
      const dimensionsLink = event.target.closest?.('a[href="#FrProductDimensions"]');
      if (dimensionsLink && this.root.contains(dimensionsLink) && !event.defaultPrevented) {
        const dimensions = document.getElementById('FrProductDimensions');
        if (!dimensions || !dimensions.querySelector('fr-product-dimensions:not([hidden])')) return;
        event.preventDefault();
        const revealDimensions = () => {
          const heading = dimensions.querySelector('h2');
          if (heading) {
            heading.setAttribute('tabindex', '-1');
            heading.focus({ preventScroll: true });
          }
          const url = new URL(window.location.href);
          url.hash = 'FrProductDimensions';
          window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
          dimensions.scrollIntoView({
            block: 'start',
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        };
        const navigateAfterClose = () => window.requestAnimationFrame(revealDimensions);
        if (this.detailDialog?.hasAttribute('open')) {
          if (typeof this.detailDialog.close === 'function') {
            // The existing close handler restores the page scroll and releases
            // body locking before the next frame moves to the dimensions.
            this.detailDialog.addEventListener('close', navigateAfterClose, { once: true });
            this.closeDetailDialog();
          } else {
            this.closeDetailDialog();
            navigateAfterClose();
          }
        } else {
          navigateAfterClose();
        }
        return;
      }
      const link = event.target.closest?.('[data-fr-detail-link]');
      if (!link || !this.root.contains(link) || event.defaultPrevented) return;
      const targetId = link.dataset.frDetailTarget;
      if (!targetId) return;
      const target = document.getElementById(targetId);
      if (!(target instanceof HTMLDetailsElement) || !this.root.contains(target)) return;

      event.preventDefault();
      this.detailTrigger = link;
      const targetPanel = target.closest('.fr-product__accordion');
      this.detailDialog?.querySelectorAll('.fr-product__accordion').forEach((item) => {
        item.hidden = item !== targetPanel;
      });
      this.detailDialog?.querySelectorAll('details[open]').forEach((item) => {
        if (item !== target) item.open = false;
      });
      target.open = true;
      if (this.detailDialogTitle) {
        this.detailDialogTitle.textContent = target.querySelector('summary h2')?.textContent?.trim() || this.root.dataset.frObjectInformation || window.frProductStrings?.information || '';
      }
      this.root.querySelectorAll('[data-fr-detail-link]').forEach((item) => {
        item.classList.toggle('is-active', item === link);
        item.setAttribute('aria-expanded', item === link ? 'true' : 'false');
      });

      if (this.detailDialog && typeof this.detailDialog.showModal === 'function') {
        if (!this.detailDialog.open) this.detailDialog.showModal();
      } else if (this.detailDialog) {
        this.detailDialog.setAttribute('open', '');
      }
      this.lockDetailScroll();

      window.requestAnimationFrame(() => {
        this.detailDialog?.querySelector('.fr-product__details-dialog-body')?.scrollTo({ top: 0, behavior: 'auto' });
        this.detailDialogClose?.focus({ preventScroll: true });
      });
    }

    sync() {
      this.customCtaLabel = this.root.dataset.frCtaLabel || '';
      const button = this.mainButton;
      const buttonLabel = this.mainButtonLabel;
      if (!button || !buttonLabel) return;

      const disabled = button.disabled || button.getAttribute('aria-disabled') === 'true';
      const needsPurchaseModeLabel =
        !disabled && (this.purchaseMode === 'preorder' || this.purchaseMode === 'made_to_order');

      if (needsPurchaseModeLabel && this.customCtaLabel && buttonLabel.textContent.trim() !== this.customCtaLabel) {
        buttonLabel.textContent = this.customCtaLabel;
      }

      if (!this.stickyButton) return;

      this.stickyButton.disabled = disabled;
      this.stickyButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');

      const stickyLabel = this.stickyButton.querySelector('span');
      const sourceLabel = buttonLabel.textContent.trim();
      const productTitle = this.root.querySelector('h1')?.textContent.trim();
      const stickyAriaLabel = [sourceLabel, productTitle].filter(Boolean).join(' — ');
      if (sourceLabel && this.stickyButton.getAttribute('aria-label') !== stickyAriaLabel) {
        this.stickyButton.setAttribute('aria-label', stickyAriaLabel);
      }
      if (stickyLabel && sourceLabel && stickyLabel.textContent.trim() !== sourceLabel) {
        stickyLabel.textContent = sourceLabel;
      }

      const selectedPlan = this.root.querySelector('[data-fr-selling-plan]')?.selectedOptions?.[0];
      const priceText = selectedPlan?.dataset.frPlanTotal || this.getVisiblePriceText();
      if (this.stickyPrice && priceText && this.stickyPrice.textContent.trim() !== priceText) {
        this.stickyPrice.textContent = priceText;
      }
    }
  }

  const initProduct = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.frProductInitialized === 'true') return;
    root.dataset.frProductInitialized = 'true';
    new FrProductController(root);
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initProduct(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initProduct);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('product-info:loaded', (event) => {
    if (event.target?.matches?.(ROOT_SELECTOR)) initProduct(event.target);
  });
})();
